/**
 * Portal API Module
 *
 * Handles Individual/Teams portal operations:
 * - Profile management
 * - Team management
 * - Subscription & billing
 * - Organization setup
 */

import { http } from './base';
import type { PortalOrganization, PortalSubscription } from './auth';

// ===== TYPES =====

export interface ModuleInfo {
  id: string;
  name: string;
  description: string;
  category: 'base' | 'integration' | 'feature' | 'support' | 'governance' | 'tier';
  monthlyPrice: number;
  yearlyPrice: number;
  monthlyPriceCents: number;
  yearlyPriceCents: number;
  isPerSeat: boolean;
  isBase: boolean;
  displayOrder: number;
  iconName: string;
  toolCount: number;
  /** Scannable bullet points sourced from MODULE_CATALOG.highlights. */
  highlights?: string[];
}

/**
 * Category label + description for the module-grid section headers.
 * Mirrors the backend CategoryMeta shape; the /billing/pricing endpoint
 * surfaces the array as `categories` so frontend components consume one
 * source of truth instead of hard-coding labels.
 */
export interface CategoryMeta {
  id: 'base' | 'integration' | 'feature' | 'support' | 'governance' | 'tier';
  label: string;
  description: string;
  order: number;
}

/**
 * A pricing-tier card (Free/Solo/Pro/Team/Enterprise). Sourced from the backend
 * TIER_CARDS via /billing/pricing (`tiers`) — same canonical data the marketing
 * /pricing page reads from the modules.json manifest.
 */
export interface TierCardInfo {
  id: string;
  name: string;
  monthlyLabel: string;
  yearlyLabel: string;
  monthlyUnit: string;
  yearlyUnit: string;
  seatCopy: string;
  tagline: string;
  ctaLabel: string;
  ctaAction: 'github-clone' | 'checkout' | 'signup' | 'contact';
  variant: 'ghost' | 'ice';
  highlight: boolean;
  badge?: string;
  features: string[];
}

export interface SubscriptionModule {
  moduleId: string;
  status: 'active' | 'pending_cancel' | 'canceled';
  priceCentsAtPurchase: number;
}

export interface TeamMember {
  id: number;
  role: 'owner' | 'admin' | 'developer' | 'stakeholder';
  status: 'pending' | 'active' | 'removed';
  joinedAt?: number;
  inviteEmail?: string;
  user: {
    id: number;
    email: string;
    name?: string;
    avatarUrl?: string;
    emailVerified: boolean;
    hasPassword: boolean;
    hasGoogle: boolean;
    hasGithub: boolean;
  } | null;
}

export interface Invoice {
  id: number;
  invoiceNumber: string;
  status: 'draft' | 'open' | 'paid' | 'void';
  total: string;
  currency: string;
  invoiceDate: number;
  paidAt?: number;
  pdfUrl?: string;
}

export const portalApi = {
  // ===== PROFILE =====

  async updateProfile(data: { name?: string }): Promise<{ success: boolean }> {
    const { data: result } = await http.put('/api/portal/auth/profile', data);
    return result;
  },

  async changePassword(data: { currentPassword: string; newPassword: string }): Promise<{ success: boolean }> {
    const { data: result } = await http.post('/api/portal/auth/change-password', data);
    return result;
  },

  async deleteAccount(feedback?: { reason: string; details?: string }): Promise<{ success: boolean }> {
    const { data: result } = await http.delete('/api/portal/auth/account', { data: feedback });
    localStorage.removeItem('portal_token');
    return result;
  },

  // ===== BILLING =====

  async getPricing(): Promise<{
    plans: {
      individual: { monthly: { price: number; label: string }; yearly: { price: number; label: string; savings: string } };
      team: { monthly: { price: number; label: string }; yearly: { price: number; label: string; savings: string } };
    };
    modules: ModuleInfo[];
    /** Category metadata for the module-grid section headers (sourced from CATEGORY_METADATA). */
    categories?: CategoryMeta[];
    /** Tier ladder (Free/Solo/Pro/Team/Enterprise) — same data as the marketing manifest. */
    tiers?: TierCardInfo[];
    seatPricing?: {
      stakeholder: {
        monthly: number;
        yearly: number;
        monthlyCents: number;
        yearlyCents: number;
        freeIncluded: number;
      };
    };
    paymentMethods: { mollie: boolean; lightning: boolean };
  }> {
    const { data } = await http.get('/api/portal/billing/pricing');
    return data;
  },

  async checkout(options: {
    plan?: 'individual' | 'team';
    interval: 'monthly' | 'yearly';
    developerSeats?: number;
    stakeholderSeats?: number;
    selectedModules?: string[];
    withTrial?: boolean;
    source?: string;
    /** Self-serve tier (Solo/Pro): charges the flat bundle price, bypassing à-la-carte. */
    tier?: 'solo' | 'pro';
  }): Promise<{ success: boolean; checkoutUrl?: string; trialStarted?: boolean; paymentId?: string; withTrial: boolean; tier?: string }> {
    const { data } = await http.post('/api/portal/billing/checkout', options);
    return data;
  },

  async getPaymentMethods(): Promise<{ methods: Array<{ id: string; description: string; image?: unknown }> }> {
    const { data } = await http.get('/api/portal/billing/payment-methods');
    return data;
  },

  async createLightningInvoice(options: {
    plan: 'individual' | 'team';
    interval: 'monthly' | 'yearly';
    developerSeats?: number;
  }): Promise<{
    success: boolean;
    invoice: string;
    paymentHash: string;
    amountSats: number;
    amountEur: string;
    expiresAt: number;
    exchangeRate: { satsPerEurCent: number; btcPerEur: number };
  }> {
    const { data } = await http.post('/api/portal/billing/lightning/invoice', options);
    return data;
  },

  async verifyLightningPayment(paymentHash: string, preimage: string): Promise<{ success: boolean; verified: boolean }> {
    const { data } = await http.post('/api/portal/billing/lightning/verify', { paymentHash, preimage });
    return data;
  },

  async getLightningStatus(paymentHash: string): Promise<{ status: 'pending' | 'paid' | 'expired' }> {
    const { data } = await http.get(`/api/portal/billing/lightning/status/${paymentHash}`);
    return data;
  },

  async getSubscription(): Promise<{
    hasSubscription: boolean;
    subscription?: PortalSubscription & {
      pricePerSeat: number;
      currency: string;
      currentPeriodStart: number;
      billingInterval: 'monthly' | 'yearly';
      modules?: SubscriptionModule[];
      baseAmount?: number;
      modulesAmount?: number;
      totalAmount?: number;
      preselectedModules?: string[];
    };
    licenseKey?: string;
  }> {
    const { data } = await http.get('/api/portal/billing/subscription');
    return data;
  },

  async updateSubscription(options: {
    developerSeats?: number;
    stakeholderSeats?: number;
  }): Promise<{ success: boolean }> {
    const { data } = await http.post('/api/portal/billing/subscription/update', options);
    return data;
  },

  async cancelSubscription(feedback?: { reason: string; details?: string }): Promise<{ success: boolean; message: string }> {
    const { data } = await http.post('/api/portal/billing/subscription/cancel', feedback || {});
    return data;
  },

  async getInvoices(): Promise<{ invoices: Invoice[] }> {
    const { data } = await http.get('/api/portal/billing/invoices');
    return data;
  },

  // ===== MODULES =====

  async getModules(): Promise<{ modules: ModuleInfo[] }> {
    const { data } = await http.get('/api/portal/billing/modules');
    return data;
  },

  async addModule(moduleId: string): Promise<{
    success: boolean;
    message: string;
    proRataCharge?: number;
    newTotal?: number;
  }> {
    const { data } = await http.post('/api/portal/billing/modules/add', { moduleId });
    return data;
  },

  async removeModule(moduleId: string): Promise<{
    success: boolean;
    message: string;
    credit?: number;
    newTotal?: number;
  }> {
    const { data } = await http.post('/api/portal/billing/modules/remove', { moduleId });
    return data;
  },

  // ===== TEAM MANAGEMENT =====

  async getTeamMembers(): Promise<{ members: TeamMember[] }> {
    const { data } = await http.get('/api/portal/team/members');
    return data;
  },

  async updateOrganizationName(name: string): Promise<{ success: boolean; name: string }> {
    const { data } = await http.put('/api/portal/team/organization', { name });
    return data;
  },

  // Org-wide shared agent memory (team conventions doc) — readable by every
  // member, writable by owner/admin. Injected into members' Pro-chat prompts.
  async getTeamMemory(): Promise<{
    content: string;
    updatedAt: number | null;
    updatedBy: string | null;
    maxChars: number;
  }> {
    const { data } = await http.get('/api/portal/team/memory');
    return data;
  },

  async updateTeamMemory(content: string): Promise<{ success: boolean; size: number; updatedAt: number }> {
    const { data } = await http.put('/api/portal/team/memory', { content });
    return data;
  },

  // ===== ENTERPRISE INQUIRIES (upgrade-request queue) =====

  // Files an org_enterprise_plan row in the admin review queue — the
  // in-product replacement for the sales mailto on the tier ladder.
  async submitEnterpriseRequest(input: { requestedSeats: number; reason?: string }): Promise<{
    success: boolean;
    message?: string;
  }> {
    const { data } = await http.post('/api/upgrade-requests/organization', input);
    return data;
  },

  async getOrgUpgradeRequests(): Promise<{
    success: boolean;
    requests: Array<{
      id: number;
      request_type: string;
      requested_amount: number;
      status: 'pending' | 'approved' | 'rejected' | 'cancelled';
      review_notes: string | null;
      created_at: number;
      reviewed_at: number | null;
    }>;
  }> {
    const { data } = await http.get('/api/upgrade-requests/organization');
    return data;
  },

  async inviteTeamMember(email: string, role: 'admin' | 'developer' | 'stakeholder'): Promise<{
    success: boolean;
    memberId: number;
    emailSent: boolean;
    message: string;
  }> {
    const { data } = await http.post('/api/portal/team/invite', { email, role });
    return data;
  },

  async acceptInvite(token: string): Promise<{
    success: boolean;
    requiresAuth?: boolean;
    inviteEmail?: string;
    organization?: PortalOrganization;
    role?: string;
  }> {
    const { data } = await http.post('/api/portal/team/invite/accept', { token });
    return data;
  },

  async resendInvite(memberId: number): Promise<{ success: boolean; message: string }> {
    const { data } = await http.post('/api/portal/team/invite/resend', { memberId });
    return data;
  },

  async updateMemberRole(memberId: number, role: 'admin' | 'developer' | 'stakeholder'): Promise<{ success: boolean }> {
    const { data } = await http.put(`/api/portal/team/members/${memberId}/role`, { role });
    return data;
  },

  async removeMember(memberId: number): Promise<{ success: boolean }> {
    const { data } = await http.delete(`/api/portal/team/members/${memberId}`);
    return data;
  },

  async leaveTeam(): Promise<{ success: boolean; message: string }> {
    const { data } = await http.post('/api/portal/team/leave');
    return data;
  },

  async getPendingInvitations(): Promise<{ invitations: Array<{
    id: number;
    organizationId: number;
    organizationName: string;
    role: string;
    inviterName: string;
    createdAt: number;
  }> }> {
    const { data } = await http.get('/api/portal/team/pending-invitations');
    return data;
  },

  async acceptInvitation(memberId: number): Promise<{ success: boolean }> {
    const { data } = await http.post('/api/portal/team/accept-invitation', { memberId });
    return data;
  },

  async declineInvitation(memberId: number): Promise<{ success: boolean }> {
    const { data } = await http.post('/api/portal/team/decline-invitation', { memberId });
    return data;
  },

  // ===== ORGANIZATION =====

  async setupOrganization(data: {
    name: string;
    planType: 'individual' | 'team' | 'enterprise';
  }): Promise<{ success: boolean; organization: PortalOrganization }> {
    const { data: result } = await http.post('/api/portal/organization/setup', data);
    return result;
  },

  async updateOrganization(updates: {
    name?: string;
    billingName?: string;
    billingEmail?: string;
    billingAddress?: string;
    billingVatId?: string;
    billingCountry?: string;
  }): Promise<{ success: boolean; organization: PortalOrganization }> {
    const { data } = await http.put('/api/portal/organization', updates);
    return data;
  },

  // ===== ONBOARDING =====

  async getOnboardingStatus(): Promise<{
    success: boolean;
    onboarding: {
      completed: boolean;
      currentStep: string;
      user: {
        id: number;
        email: string;
        name?: string;
        emailVerified: boolean;
      };
      organization?: {
        id: number;
        name: string;
        planType: 'individual' | 'team' | 'enterprise';
      };
      subscription?: {
        status: string;
        plan: string;
        trialEndsAt?: number;
      };
    };
  }> {
    const { data } = await http.get('/api/portal/auth/onboarding');
    return data;
  },

  async updateOnboardingStep(step: string): Promise<{
    success: boolean;
    onboarding: { currentStep: string; completed: boolean };
  }> {
    const { data } = await http.put('/api/portal/auth/onboarding', { step });
    return data;
  },

  /**
   * `skippedFrom` is the step the user pressed "Skip all" on, when they did.
   * Without it the backend cannot tell a completed wizard from a skipped one:
   * skipping navigates to 'complete' first, so by the time this call lands the
   * stored step says 'complete' either way. That is why every production user
   * has onboarding_skipped = 0 while most never connected anything.
   */
  async completeOnboarding(skippedFrom?: string): Promise<{ success: boolean; message: string }> {
    const { data } = await http.post('/api/portal/auth/onboarding/complete',
      skippedFrom ? { skippedFrom } : {});
    return data;
  },

  async savePreselectedModules(modules: string[]): Promise<{ success: boolean; preselectedModules: string[] }> {
    const { data } = await http.put('/api/portal/auth/onboarding/modules', { modules });
    return data;
  },

  // ===== SERVICENOW INSTANCES =====

  async getServiceNowInstances(): Promise<{
    success: boolean;
    instances: Array<{
      id: number;
      instanceName: string;
      instanceUrl: string;
      environmentType: string;
      clientId: string;
      isDefault: boolean;
      enabled: boolean;
      lastTestedAt?: number;
      lastTestStatus?: string;
      lastTestMessage?: string;
      createdAt: number;
      updatedAt: number;
    }>;
  }> {
    const { data } = await http.get('/api/servicenow/instances');
    return data;
  },

  async createServiceNowInstance(instance: {
    instanceName: string;
    instanceUrl: string;
    environmentType: string;
    /** Defaults to 'oauth' server-side when omitted. */
    authMethod?: 'oauth' | 'basic';
    clientId?: string;
    clientSecret?: string;
    username?: string;
    password?: string;
    isDefault?: boolean;
  }): Promise<{
    success: boolean;
    instance: {
      id: number;
      instanceName: string;
      instanceUrl: string;
      environmentType: string;
      clientId: string;
      isDefault: boolean;
      enabled: boolean;
      createdAt: number;
      updatedAt: number;
    };
  }> {
    const { data } = await http.post('/api/servicenow/instances', instance);
    return data;
  },

  async updateServiceNowInstance(
    instanceId: number,
    updates: {
      instanceName?: string;
      instanceUrl?: string;
      environmentType?: string;
      clientId?: string;
      clientSecret?: string;
      isDefault?: boolean;
      enabled?: boolean;
    }
  ): Promise<{ success: boolean; instance: any }> {
    const { data } = await http.put(`/api/servicenow/instances/${instanceId}`, updates);
    return data;
  },

  async deleteServiceNowInstance(instanceId: number): Promise<{ success: boolean; message: string }> {
    const { data } = await http.delete(`/api/servicenow/instances/${instanceId}`);
    return data;
  },

  /**
   * Two success shapes: the OAuth path returns `success` + `message` at the
   * top level, the basic-auth path additionally nests a `testResult`.
   * Failures come back as 4xx, so callers see them as a thrown error.
   */
  async testServiceNowInstance(instanceId: number): Promise<{
    success: boolean;
    message?: string;
    testResult?: { success: boolean; message: string };
  }> {
    const { data } = await http.post(`/api/servicenow/instances/${instanceId}/test`);
    return data;
  },
};
